import React from 'react';
import {
  Box, Container, Heading, Text, Button, VStack, HStack, SimpleGrid, Icon, Badge, Flex, List, ListItem, ListIcon, Image,
} from '@chakra-ui/react';
import { FiCheck, FiTruck, FiTool, FiMail } from 'react-icons/fi';

export const POS_KIT_PRICE = 2249;
export const POS_KIT_EMAIL = 'info@smartlaundrybasket.ai';

export const POS_HARDWARE = [
  {
    image: '/pos-kit/tablet.jpg',
    title: 'POS tablet',
    desc: 'The counter screen. Staff weigh the bag, pick the price, and take payment. It arrives signed in to your wash and fold business.',
  },
  {
    image: '/pos-kit/receipt-printer.jpg',
    title: 'Receipt printer',
    desc: 'Prints the customer receipt: order number, weight, and total.',
  },
  {
    image: '/pos-kit/label-printer.jpg',
    title: 'Label printer',
    desc: 'Prints the bag tag: order number, customer name, due date, and a code staff can scan with a phone.',
  },
  {
    image: '/pos-kit/cash-drawer.jpg',
    title: 'Cash drawer',
    desc: 'Pops open on a cash sale. Every open is logged.',
  },
];

const POSKitSection = () => {
  const mailto = `mailto:${POS_KIT_EMAIL}?subject=${encodeURIComponent('POS Kit order')}&body=${encodeURIComponent(
    "Hi, I'd like to order the $2,249 POS Kit.\n\nBusiness name:\nShipping address:\nPhone:\n"
  )}`;

  return (
    <Box id="pos-kit" py={{ base: 16, md: 20 }} bg="white">
      <Container maxW="1200px">
        <VStack spacing={4} textAlign="center" mb={10}>
          <Badge colorScheme="orange" px={3} py={1} borderRadius="full">Counter kit · ${POS_KIT_PRICE.toLocaleString()} once</Badge>
          <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>The counter: tablet, receipt printer, label printer, cash drawer.</Heading>
          <Text color="gray.600" maxW="760px" fontSize="lg">
            A wash and fold business rings bags on these four pieces. We load your POS on the tablet, test the printers and the drawer, and ship the box. You plug it in and take the next bag.
          </Text>
        </VStack>

        <Flex direction={{ base: 'column', lg: 'row' }} gap={8} align="stretch">
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={5} flex={3}>
            {POS_HARDWARE.map((item) => (
              <Box key={item.title} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Image src={item.image} alt={item.title} w="100%" h="200px" objectFit="cover" bg="gray.50" />
                <Box p={4}>
                  <Text fontWeight="700">{item.title}</Text>
                  <Text color="gray.600" fontSize="sm" mt={1}>{item.desc}</Text>
                </Box>
              </Box>
            ))}
          </SimpleGrid>

          <Box flex={2} bg="white" borderRadius="2xl" p={8} boxShadow="lg" border="2px solid" borderColor="orange.200" display="flex" flexDirection="column">
            <Text fontWeight="700" fontSize="lg" mb={1}>Complete counter kit</Text>
            <Heading fontSize="5xl" color="orange.500" mb={1}>${POS_KIT_PRICE.toLocaleString()}</Heading>
            <Text color="gray.500" fontSize="sm" mb={6}>One-time. The software price does not change.</Text>
            <List spacing={3} fontSize="sm" mb={6}>
              <ListItem><ListIcon as={FiCheck} color="green.500" />Tablet and stand, receipt printer, label printer, cash drawer</ListItem>
              <ListItem><ListIcon as={FiCheck} color="green.500" />Loaded for your wash and fold business</ListItem>
              <ListItem><ListIcon as={FiTruck} color="green.500" />Setup and shipping included</ListItem>
              <ListItem><ListIcon as={FiCheck} color="green.500" />Same software: free under $1,000 a month, then $49, never above $149</ListItem>
            </List>
            <HStack align="flex-start" spacing={3} p={4} borderRadius="xl" bg="orange.50" mb={6}>
              <Icon as={FiTool} boxSize={5} color="orange.500" mt={0.5} />
              <Text fontSize="sm" color="gray.700">We configure it, test a receipt, a bag tag, and a drawer pop, then ship it to you.</Text>
            </HStack>
            <Button as="a" href={mailto} colorScheme="orange" size="lg" borderRadius="full" leftIcon={<FiMail />} mt="auto">
              Order the counter kit
            </Button>
            <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
              Email <strong>{POS_KIT_EMAIL}</strong> with your business name and shipping address.
            </Text>
            <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
              Already have a tablet or printers? The software runs in a browser. The kit is optional.
            </Text>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default POSKitSection;
